import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize, take } from 'rxjs';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { BugReportService, BugReportType } from '../../../core/services/bug-report.service';

interface BugTypeOption {
  value: BugReportType;
  label: string;
}

@Component({
  selector: 'app-bug-report-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSnackBarModule
  ],
  templateUrl: './bug-report-dialog.component.html',
  styleUrl: './bug-report-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BugReportDialogComponent {
  private formBuilder = inject(FormBuilder);
  private bugReportService = inject(BugReportService);
  private dialogRef = inject(MatDialogRef<BugReportDialogComponent>);
  private snackBar = inject(MatSnackBar);
  private readonly initialDisableClose = this.dialogRef.disableClose;

  readonly bugTypeOptions: BugTypeOption[] = [
    { value: 'ui', label: 'UI / Display' },
    { value: 'gameplay', label: 'Gameplay' },
    { value: 'connection', label: 'Connection' },
    { value: 'performance', label: 'Performance' },
    { value: 'data', label: 'Game Data' },
    { value: 'other', label: 'Other' }
  ];

  readonly form = this.formBuilder.group({
    bugType: this.formBuilder.control<BugReportType | null>(null, [Validators.required]),
    description: this.formBuilder.nonNullable.control('', [Validators.required, Validators.minLength(10), Validators.maxLength(1000)]),
    contactMe: this.formBuilder.nonNullable.control(false)
  });

  isSubmitting = false;
  submitError: string | null = null;

  get descriptionLength(): number {
    return this.form.controls.description.value.length;
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onSubmit(): void {
    if (this.form.invalid || this.isSubmitting) {
      this.form.markAllAsTouched();
      return;
    }

    const { bugType, description, contactMe } = this.form.getRawValue();
    if (!bugType) {
      return;
    }

    this.submitError = null;
    this.isSubmitting = true;
    this.dialogRef.disableClose = true;

    this.bugReportService.submitReport(bugType, description, contactMe)
      .pipe(
        take(1),
        finalize(() => {
          this.isSubmitting = false;
          this.dialogRef.disableClose = this.initialDisableClose;
        })
      )
      .subscribe({
        next: () => {
          this.dialogRef.close(true);
          this.snackBar.open('Bug report submitted — thank you!', 'Close', { duration: 4000 });
        },
        error: (error: unknown) => {
          const isRateLimitError = (error instanceof HttpErrorResponse && error.status === 429)
            || (error instanceof Error && error.message.includes('Error Code: 429'));

          if (isRateLimitError) {
            this.submitError = 'You have reached the daily bug report limit. Please try again tomorrow.';
            return;
          }

          this.submitError = 'Could not submit your bug report right now. Please try again.';
        }
      });
  }
}
